import { PayloadAction } from '@reduxjs/toolkit';
import {
  AuthActions,
  AuthSelectors,
  ConfigSelectors,
  HistogramSelectors,
  LanguageSelectors,
  LanguageService,
  NotificationActions,
  ProgressActions,
  ProgressSelectors,
  RemoteConfigService,
  StatsActions,
  StatsSelectors,
  getWorkoutId,
  waitForExperimentsFeature,
  waitForFirebaseReadySaga,
  waitForFullProgressSaga,
} from '@web_opxp/features';
import { Lottie } from '@web_opxp/lottie';
import { all, call, delay, fork, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { WorkoutUnityResult } from 'web_unity_engine';
import { AchievementsActions, AchievementsSelectors, CELEBRATION_LOTTIES } from '../achievements';
import {
  ASSESSMENT_SESSION_ID,
  CATEGORY_CHANGE_STAT,
  DEFAULT_CATEGORY_ID,
  EXERCISE_CATEGORY_STAT,
  LED_CONSENT_STAT,
} from '../base/constants';
import ENV from '../base/env';
import { isOnPage, redirectInternal, redirectToSubscribe } from '../base/historyService';
import { EExerciseCategory } from '../base/types';
import { makeCategoryChangeStatString, makeExerciseCategoryStatString } from '../base/utils';
import { ChallengeSelectors } from '../challenge';
import { CyclesActions, CyclesSelectors } from '../cycles';
import { FeedbackActions } from '../feedback';
import { HighscoresActions } from '../highscores';
import { SessionActions, SessionSelectors, SessionTypes } from '../session';
import { SuccessPredictorActions } from '../successPredictor';
import { UnityActions, UnitySelectors, UnityUtils } from '../unity';
import {
  EExerciseMode,
  EExerciseView,
  WORKOUTS_SUPPORTING_EXERCISE_RUN,
  ledConfirmationLottie,
  unityPreloaderLottie,
} from './constants';
import * as FirebaseActions from './firebaseActions';
import * as Selectors from './selectors';
import { ExerciseActions } from './slice';
import {
  EExtraMode,
  ENeededAdjustment,
  ExerciseError,
  IFullFeedbackResult,
  IInternalFeedbackResult,
  IOpenExercisePayload,
} from './types';
import { getMaxThreshold, getMinThreshold } from './utils';

export function* waitForSession() {
  const sessionIsLoading = yield select(SessionSelectors.isLoading);
  if (!sessionIsLoading) {
    return;
  }
  yield take(SessionActions.updateWorkoutStates);
}

export function* waitForStats() {
  const statsLoading = yield select(StatsSelectors.isStatsLoading);
  if (!statsLoading) {
    return;
  }
  yield take(StatsActions.statsReady);
}

export function* waitForCycles() {
  // wait until cycles state are loaded
  const cyclesLoading = yield select(CyclesSelectors.isCyclesLoading);
  if (cyclesLoading) {
    yield take(CyclesActions.cyclesReady);
  }
}

export function* checkTokenExpiration() {
  const token = yield select(AuthSelectors.getToken);
  const now = Date.now();
  let expires: number;
  try {
    const userData = JSON.parse(window.atob(token.split('.')[1]));
    expires = parseInt(userData.exp, 10) * 1000;
  } catch (error) {
    // if parsing of the token fails (token invalid) - redirect to sso
    yield put(AuthActions.redirectToSSO());
    return;
  }
  const minutesLeft = Math.floor((expires - now) / 1000 / 60);
  if (minutesLeft < 30) {
    // if token almost expired - redirect to sso to refresh it
    yield put(AuthActions.redirectToSSO());
  }
}

export function* waitForWorkoutStats(actualWorkoutId: number, siblings: number[]) {
  let loaded = false;
  while (!loaded) {
    // progress must be loaded for all siblings of the given workout
    const progressLoaded = yield select(ProgressSelectors.isLastProgressLoadedForWorkouts, siblings);
    const statesLoaded = !(yield select(SessionSelectors.isLoading));
    const workoutStatsLoaded = yield select(SessionSelectors.statsLoaded, actualWorkoutId);
    loaded = progressLoaded && statesLoaded && workoutStatsLoaded;
    if (!loaded) {
      yield delay(300);
    }
  }
}

export function* checkUsersExercise(
  categoryGroupId: number,
  isChallenge: boolean,
  activeWorkoutId: number,
  actualWorkoutId: number,
  workoutIndex: number | undefined
) {
  yield call(waitForFirebaseReadySaga);
  yield call(waitForExperimentsFeature);
  const challengeWorkoutId = yield select(ConfigSelectors.getChallengeWorkoutIdByCategoryGroupId, categoryGroupId);
  const todaysChallengeWorkoutId: number | null = yield select(ChallengeSelectors.getTodaysChallengeWorkoutId);
  if (todaysChallengeWorkoutId !== challengeWorkoutId) {
    // wrong challenge workout id (probably from previous days)
    // disable challenge and continue with normal flow to replace workout
    isChallenge = false;
  } else {
    // last progress for challenge workout must be loaded by session saga
    const alreadyParticipated = yield select(ChallengeSelectors.hasCompletedTodaysChallenge, challengeWorkoutId);
    if (alreadyParticipated) {
      // disable challenge and continue with normal flow
      isChallenge = false;
    } else if (challengeWorkoutId !== activeWorkoutId) {
      // set challenge workout as actual workout and replace current in cycle
      actualWorkoutId = challengeWorkoutId;
      yield put(CyclesActions.replaceCurrentWorkout(challengeWorkoutId, workoutIndex));
    }
  }
  return { isChallenge, actualWorkoutId };
}

export function* replaceWorkouts(categoryGroupId: number, activeWorkoutId: number, workoutIndex: number | undefined) {
  const categoryIdStat: EExerciseCategory | undefined = yield select(
    StatsSelectors.getStatIntValueById,
    makeExerciseCategoryStatString(categoryGroupId)
  );
  // if stat is not set use default category (normal difficulty)
  const categoryId = categoryIdStat ?? DEFAULT_CATEGORY_ID;
  const workoutId = getWorkoutId(categoryId, categoryGroupId);
  if (workoutId !== activeWorkoutId) {
    // try to find given workoutId in current session
    const canReplace = yield select(CyclesSelectors.areWorkoutIdAndIndexCorrect, workoutId, workoutIndex);
    // check if we can replace workout to prevent catching error
    if (!canReplace) {
      return redirectInternal('/');
    }
    yield put(CyclesActions.replaceCurrentWorkout(workoutId, workoutIndex));
  }
  return workoutId;
}

export function* openExercisePageSaga(action: PayloadAction<IOpenExercisePayload>) {
  const { exerciseLink, workoutIndex } = action.payload;
  yield call(checkTokenExpiration);
  yield call(waitForCycles);
  const sessionId = yield select(CyclesSelectors.getCurrentSessionId);
  if (sessionId === ASSESSMENT_SESSION_ID) {
    // redirect to dashboard if assessment not finished
    return redirectInternal('/');
  }
  // init caching preloader lottie
  yield put(Lottie.Actions.lottieForceCacheAction(unityPreloaderLottie));
  yield put(SessionActions.loadSession());
  yield call(waitForSession);
  const sessionCompleted = yield select(SessionSelectors.allWorkoutsCompleted);
  if (sessionCompleted) {
    yield put(ExerciseActions.completeSession());
    return;
  }
  // ensure that stats are loaded
  yield call(waitForStats);
  const activeWorkoutId: number = yield select(SessionSelectors.getActiveWorkoutId);
  let actualWorkoutId = activeWorkoutId;
  let isChallenge: boolean = yield select(ConfigSelectors.isChallengeExerciseId, exerciseLink);
  if (exerciseLink) {
    const categoryGroupId: number = yield select(ConfigSelectors.getCategoryGroupId, exerciseLink);
    if (isChallenge) {
      // check if user already did this exercise today
      const checkResult: { isChallenge: boolean; actualWorkoutId: number } = yield call(
        checkUsersExercise,
        categoryGroupId,
        isChallenge,
        activeWorkoutId,
        actualWorkoutId,
        workoutIndex
      );
      isChallenge = checkResult.isChallenge;
      actualWorkoutId = checkResult.actualWorkoutId;
    }
    if (!isChallenge) {
      actualWorkoutId = yield call(replaceWorkouts, categoryGroupId, activeWorkoutId, workoutIndex);
    }
  }
  // wait for progress and session data to be loaded
  // get sibling Ids
  const siblings: number[] = yield select(ConfigSelectors.getWorkoutIdsOfCategoryGroup, actualWorkoutId);
  // load success predictor data
  yield put(SuccessPredictorActions.loadSuccessPredictor(siblings));
  // load last progress data
  yield put(ProgressActions.loadLastProgress(siblings));
  // wait for progress and workoutstats
  yield call(waitForWorkoutStats, actualWorkoutId, siblings);

  // define difficulty
  const lastProgress = yield select(ProgressSelectors.getWorkoutLastProgressWithSiblings, siblings);
  const nextDifficulty: number = yield select(SessionSelectors.getNextDifficultyForSiblings, siblings, lastProgress);
  const maxDifficulty: number | null = yield select(SessionSelectors.getReachedDifficulty, actualWorkoutId);
  yield put(ExerciseActions.initDifficulty(nextDifficulty, maxDifficulty ?? 0));
  // define goal
  const goal = yield select(SessionSelectors.getCurrentWorkoutGoal, actualWorkoutId);
  yield put(ExerciseActions.setGoal(goal));
  // define last score
  const score = lastProgress ? lastProgress.result : 0;
  yield put(ExerciseActions.setLastScore(score));
  // preload amount of gameplays / exerciseRun
  yield call(preloadExerciseRun, actualWorkoutId, siblings);
  // set page state to ready
  yield put(ExerciseActions.exercisePageReady(actualWorkoutId));
  yield fork(startUnitySaga, actualWorkoutId);
  // start loading achievements if not yet done
  yield put(AchievementsActions.loadAchievements());
  // precache achievement lotties
  for (const lottie of CELEBRATION_LOTTIES) {
    yield put(Lottie.Actions.lottieForceCacheAction(lottie));
  }
}

export function* preloadExerciseRun(workoutId: number, siblings: number[]) {
  if (!WORKOUTS_SUPPORTING_EXERCISE_RUN.includes(workoutId)) {
    // workout does not support exercise run - we don't need to load anything
    return;
  }
  yield call(waitForFullProgressSaga);
  const amountOfGameplays = yield select(ProgressSelectors.getCompletionCountWithSiblings, siblings);
  // for defined exercises exerciseRun must be amount of gameplays + 1
  // see BW-8371
  yield put(ExerciseActions.setExerciseRun(amountOfGameplays + 1));
}

export function* startUnitySaga(workoutId: number) {
  // preload firebase
  yield call(waitForFirebaseReadySaga);
  // get unity path from firebase
  const unityPath: string = yield call(RemoteConfigService.getStringConfigValue, ENV.RemoteConfigUnity, ENV.UnityPath);
  // start loading unity and let know that its training exercise
  yield put(UnityActions.initUnity('training', undefined, unityPath));
  const isStarted: boolean = yield select(UnitySelectors.isUnityStarted);
  // skip waiting if already started before
  if (!isStarted) {
    yield take(UnityActions.unityStarted);
  }
  // preload balancing file for given exercise
  const isChallenge = yield select(ConfigSelectors.isChallengeWorkout, workoutId);
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId, isChallenge);
  const exerciseJsonFile = yield select(ConfigSelectors.getExerciseJsonFile, workoutId);
  yield put(UnityActions.loadBalancing(exerciseId, exerciseJsonFile));
}

export function* hideUnityPreloaderLottie() {
  // initiate showing the lottie
  yield put(ExerciseActions.showUnityPreloaderLottie());
  // wait until lottie animation is completed
  let lottieCompleted = false;
  while (!lottieCompleted) {
    const action: Lottie.ActionTypes.ILottieCompleteAction = yield take(Lottie.ActionTypes.LOTTIE_COMPLETE);
    lottieCompleted = action.id === 'brain_in';
  }
  // hide preloader layer completely
  yield put(ExerciseActions.hideUnityPreloader());
}

export function* startExerciseSaga(action: PayloadAction<number>) {
  const difficulty = action.payload;
  // show preloader lottie
  yield put(ExerciseActions.showUnityPreloader());
  const workoutId = yield select(Selectors.getWorkoutId);
  const isChallenge = yield select(ConfigSelectors.isChallengeWorkout, workoutId);
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId, isChallenge);
  yield put(UnityActions.prepareExercise(exerciseId, workoutId));
  yield take(UnityActions.exerciseReady);
  // hide preloader lottie
  yield call(hideUnityPreloaderLottie);
  // set runtime params and start workout
  const duration = yield select(ConfigSelectors.getWorkoutDuration, workoutId);
  const goal = yield select(Selectors.getLastWorkoutGoal);
  const exerciseRun = yield select(Selectors.getExerciseRun);
  // goal must be integer in unity, to ensure that it is integer we round it before sending to unity
  yield call(checkTokenExpiration);
  yield put(UnityActions.startExercise(difficulty, duration, Math.round(goal), exerciseRun));
}

export function* startLearningModeSaga(action: PayloadAction<number>) {
  const difficulty = action.payload;
  // show preloader lottie
  yield put(ExerciseActions.showUnityPreloader());
  const workoutId = yield select(Selectors.getWorkoutId);
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId);
  yield put(UnityActions.prepareExercise(exerciseId, workoutId));
  yield take(UnityActions.exerciseReady);
  // hide preloader lottie
  yield call(hideUnityPreloaderLottie);
  yield put(UnityActions.startLearning(difficulty));
}

export function* completeWorkoutSaga(action: PayloadAction<WorkoutUnityResult>) {
  const result = action.payload;
  const sessionId = yield select(CyclesSelectors.getCurrentSessionId);
  if (sessionId === ASSESSMENT_SESSION_ID) {
    //return if we are doing assessment
    return;
  }
  // learning mode always ends with cancelled exit code
  if (UnityUtils.isWorkoutCancelled(result)) {
    yield put(ExerciseActions.restartExercise());
    return;
  }
  // show lottie for time saving/loading goes on
  yield put(ExerciseActions.showUnityPreloader());
  // we need to preload full progress needed for achievements detection
  // and for post-workout progress widget
  const workoutId = yield select(Selectors.getWorkoutId);
  // find total amount of gameplays - for this we need to load full progress
  yield call(waitForFullProgressSaga);
  // save user result and update cycle
  yield call(saveProgressAndCycle, result);
  const isChallenge = yield select(ConfigSelectors.isChallengeWorkout, workoutId);
  if (isChallenge) {
    yield put(SessionActions.loadSession());
    yield call(waitForSession);
    // hide lottie and active view to let achievement view show
    yield call(hideUnityPreloaderLottie);
    yield put(ExerciseActions.changeView(EExerciseView.ChallengeExerciseCompleted));
    return;
  }
  // save workout state
  yield call(updateSession, result);
  // detect achievements
  yield call(detectAchievements, result);
  // adjust difficulty if needed
  const balancingWasChanged = yield call(balancingWasChangedManually);
  if (balancingWasChanged) {
    // start loading required lottie
    yield put(Lottie.Actions.lottieForceCacheAction(ledConfirmationLottie));
    // show change confirmation screen
    yield put(ExerciseActions.confirmDifficultyChange());
  } else {
    // detect anomaly and change or ask for change
    yield call(difficultyAdjustmentSaga);
  }
  // hide lottie
  yield call(hideUnityPreloaderLottie);
  // switch view to evaluation
  yield put(ExerciseActions.completeExercise());
  // Check feedback status (which explains if should show feedback dialog)
  yield put(FeedbackActions.checkFeedbackStatus());
}

export function* detectAchievements(result: WorkoutUnityResult) {
  // this saga must collect all data necessary for achievements calculation
  // defined by the action builder types
  // send them to achievements feature
  // and wait for calculation to finish
  const workoutId = yield select(Selectors.getWorkoutId);
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId);
  const siblings: number[] = yield select(ConfigSelectors.getWorkoutIdsOfCategoryGroup, workoutId);
  // find total amount of gameplays
  // progress must be loaded before
  const totalGameplays: number = yield select(ProgressSelectors.getCompletionCountWithSiblings, siblings);
  // get max difficulty reached
  const maxDifficulty: number = yield select(UnitySelectors.getMaxDifficulty);
  // get score from latest result
  const score = result.stats.score;
  // calculate new comparison value
  const comparison = yield select(HistogramSelectors.getWorkoutComparison, workoutId, score);
  // get workout goal
  const goal = yield select(SessionSelectors.getCurrentWorkoutGoal, workoutId);
  // get highest score for siblings
  const highscroreResult: number | undefined = yield select(ProgressSelectors.getHighscoreResultWithSiblings, siblings);
  yield put(
    AchievementsActions.detectAchievements(
      workoutId,
      exerciseId,
      siblings,
      result,
      maxDifficulty,
      comparison,
      totalGameplays,
      goal,
      highscroreResult
    )
  );
  // wait for detection to complete
  yield take(AchievementsActions.achievementsReady);
  const hasNewAchievements = yield select(AchievementsSelectors.hasNewAchievements);
  if (!hasNewAchievements) {
    // if no achievements are detected - continue to the next screen
    return;
  }
  // hide lottie and active view to let achievement view show
  yield put(ExerciseActions.changeView(EExerciseView.Celebration));
  // wait for achievements screen to hide
  yield take(AchievementsActions.hideAchievements);
  // show unity lottie again in case led feature will take some time
  yield put(ExerciseActions.showUnityPreloader());
}

export function* saveProgressAndCycle(result: WorkoutUnityResult) {
  yield call(waitForFullProgressSaga);
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
  const sessionId = yield select(CyclesSelectors.getCurrentSessionId);
  const workoutId = yield select(Selectors.getWorkoutId);
  const variant = yield select(ConfigSelectors.getConfigVariant);
  const version = yield select(ConfigSelectors.getConfigVersion);
  // save progress
  yield put(ProgressActions.saveProgress(variant, version, cycleId, sessionId, workoutId, result));
  // to prevent completing cycle without properly saved progress
  const workoutIndex = yield select(SessionSelectors.getActiveWorkoutRelativeIndex);
  // update cycle
  yield put(CyclesActions.completeWorkout(workoutId, workoutIndex));
  // wait for saving to be finished
  yield take(CyclesActions.cycleSaved);
}

export function* updateSession(result: WorkoutUnityResult) {
  const workoutId = yield select(Selectors.getWorkoutId);
  // update comparison value in current session
  // get score from latest result
  const score = result.stats.score;
  // calculate new comparison value
  const comparison = yield select(HistogramSelectors.getWorkoutComparison, workoutId, score);
  yield put(SessionActions.updateWorkoutComparison(workoutId, Math.floor(comparison)));
  // update workout states in session: save next goal and difficulty values
  // calculate goal from progress of siblings
  const siblings = yield select(SessionSelectors.getCurrentWorkoutSiblings, workoutId);
  const goal = yield select(ProgressSelectors.getNextWorkoutGoalWithSibling, siblings);
  // get difficulty params from gameplay result
  const startDifficulty = result.stats.startDifficulty;
  const endDifficulty = result.stats.endDifficulty;
  // define the biggest difficulty reached
  const oldReachDifficulty: number | null = yield select(SessionSelectors.getReachedDifficulty, workoutId);
  const reachDifficulty = Math.max(oldReachDifficulty ?? 0, endDifficulty);
  // save workout state
  yield put(SessionActions.saveWorkoutState(workoutId, goal, startDifficulty, reachDifficulty));
  yield call(waitForSession);
}

export function* difficultyAdjustmentSaga() {
  // detect anomaly in last progress data
  const workoutId = yield select(Selectors.getWorkoutId);
  const lastProgressAccuracy: number | null = yield select(ProgressSelectors.getLastWorkoutAccuracy, workoutId);
  if (lastProgressAccuracy === null) {
    return;
  }
  yield call(waitForFirebaseReadySaga);
  const categoryGroupId = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, workoutId);
  const minThreshold: number = yield call(getMinThreshold, categoryGroupId);
  const maxThreshold: number = yield call(getMaxThreshold, categoryGroupId);
  const highAccuracy = lastProgressAccuracy > maxThreshold;
  const lowAccuracy = lastProgressAccuracy < minThreshold;
  if (!highAccuracy && !lowAccuracy) {
    // no anomaly detected
    return;
  }
  const neededAdjustment = highAccuracy ? ENeededAdjustment.Higher : ENeededAdjustment.Lower;
  const adjustmentPossible = yield call(canAdjustDifficulty, workoutId, neededAdjustment);
  if (!adjustmentPossible) {
    // adjustment not possible (highest or lowest difficulty reached)
    return;
  }
  const ledStat: number | undefined = yield select(StatsSelectors.getStatIntValueById, LED_CONSENT_STAT);
  const isManualAdjustmentChosen = typeof ledStat !== 'undefined' ? ledStat === 0 : true;
  if (!isManualAdjustmentChosen) {
    // automatic adjustment
    yield call(adjustExerciseDifficulty, workoutId, neededAdjustment);
    return;
  }
  if (neededAdjustment === ENeededAdjustment.Higher) {
    yield put(ExerciseActions.easyDifficulty());
  } else {
    yield put(ExerciseActions.hardDifficulty());
  }
}

export function* balancingWasChangedManually() {
  const workoutId = yield select(Selectors.getWorkoutId);
  const categoryGroupId: number = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, workoutId);
  const categoryStat: number | undefined = yield select(
    StatsSelectors.getStatIntValueById,
    makeCategoryChangeStatString(categoryGroupId)
  );
  return categoryStat !== undefined && categoryStat !== -1;
}

export function* canAdjustDifficulty(workoutId: number, adjustment: ENeededAdjustment) {
  const currentCategoryId: EExerciseCategory = yield select(ConfigSelectors.getWorkoutCategoryId, workoutId);
  const futureCategoryId: EExerciseCategory | -1 = yield call(getAdjustedCategory, currentCategoryId, adjustment);
  return futureCategoryId !== -1;
}

export function getAdjustedCategory(categoryId: EExerciseCategory, adjustment: ENeededAdjustment) {
  if (categoryId === EExerciseCategory.Easy) {
    if (adjustment === ENeededAdjustment.Lower) {
      // category cannot be lower than easy
      return -1;
    }
    return EExerciseCategory.Medium;
  }
  if (categoryId === EExerciseCategory.Hard) {
    if (adjustment === ENeededAdjustment.Higher) {
      // category cannot be higher than hard
      return -1;
    }
    return EExerciseCategory.Medium;
  }
  // categoryId === EExerciseCategory.Medium
  if (adjustment === ENeededAdjustment.Higher) {
    return EExerciseCategory.Hard;
  }
  return EExerciseCategory.Easy;
}

export function* adjustExerciseDifficulty(workoutId: number, adjustment: ENeededAdjustment) {
  const currentCategoryId: EExerciseCategory = yield select(ConfigSelectors.getWorkoutCategoryId, workoutId);
  const futureCategoryId: EExerciseCategory | -1 = yield call(getAdjustedCategory, currentCategoryId, adjustment);
  if (futureCategoryId === -1) {
    // category can not be changed (limit reached)
    // this check was made before already, but better to check twice then to get incorrect data
    return;
  }
  yield call(updateCategoryForWorkout, workoutId, futureCategoryId);
  yield call(preserveReachedDifficulty, workoutId, futureCategoryId);
}

export function* updateCategoryForWorkout(workoutId: number, categoryId: EExerciseCategory) {
  const categoryGroupId: number = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, workoutId);
  // wait if stats are saving still
  yield call(waitForStatsSaving);
  // save new category id in stats
  yield put(
    StatsActions.updateGenericStat(EXERCISE_CATEGORY_STAT, {
      statId: categoryGroupId,
      valueInt: categoryId as number,
      valueTxt: '',
    })
  );
  yield put(StatsActions.saveStatsQueue());
  yield call(waitForStatsSaving, 'Difficulty adjustment can not be saved');
  // replace old workut id with the new one
  const targetWorkoutId = getWorkoutId(categoryId, categoryGroupId);
  yield call(replaceWorkoutSaga, workoutId, targetWorkoutId);
}

export function* waitForStatsSaving(messageOnError = 'Waiting for saving Stats failed') {
  const statsSaving = yield select(StatsSelectors.isSaving);
  if (!statsSaving) {
    return;
  }
  const result = yield take([StatsActions.saveStatsQueueSuccess, StatsActions.saveStatsQueueFailed]);
  if (result.type === StatsActions.saveStatsQueueFailed.type) {
    throw new ExerciseError(messageOnError);
  }
}

export function* preserveReachedDifficulty(currentWorkoutId: number, nextCategoryId: EExerciseCategory) {
  // get state for current workout
  const currentWorkoutState: SessionTypes.ITrainingWorkoutState | undefined = yield select(
    SessionSelectors.getWorkoutState,
    currentWorkoutId
  );
  if (!currentWorkoutState) {
    return;
  }
  // calculate next workout id
  const categoryGroupId: number = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, currentWorkoutId);
  const nextWorkoutId: number = getWorkoutId(nextCategoryId, categoryGroupId);
  // copy and save workout state for next workout
  const { goal, reachDifficulty, startDifficulty } = currentWorkoutState;
  yield put(SessionActions.saveWorkoutState(nextWorkoutId, goal, startDifficulty, reachDifficulty));
}

export function* replaceWorkoutSaga(sourceId: number, targetId: number) {
  // call rebuild training to rebuild the current cycle
  yield put(CyclesActions.replaceWorkout(sourceId, targetId));
  const result = yield take([CyclesActions.replaceWorkoutSuccess, CyclesActions.replaceWorkoutFailed]);
  if (result.type === CyclesActions.replaceWorkoutFailed.type) {
    throw new ExerciseError('Can not update training after difficulty adjustment!');
  }
  const sessionComplete = yield select(SessionSelectors.allWorkoutsCompleted);
  if (!sessionComplete) {
    // reload current session if its not completed
    yield put(SessionActions.reloadSession());
    yield call(waitForSession);
  }
}

export function* saveDifficultyChangeAnswerSaga(action: PayloadAction<boolean>) {
  const answer = action.payload;
  const t = LanguageService.getTFunction();
  const workoutId: number = yield select(Selectors.getWorkoutId);
  const categoryGroupId: number = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, workoutId);
  const currentCategoryId: EExerciseCategory = yield select(ConfigSelectors.getWorkoutCategoryId, workoutId);
  const categoryStat: number | undefined = yield select(
    StatsSelectors.getStatIntValueById,
    makeCategoryChangeStatString(categoryGroupId)
  );
  const oldCategoryId: EExerciseCategory | -1 = categoryStat as EExerciseCategory | -1;
  if (oldCategoryId === -1) {
    // do not change if nothing was changed
    return;
  }
  // regardless of the answer we need to reset category change stat
  yield put(
    StatsActions.updateGenericStat(CATEGORY_CHANGE_STAT, {
      statId: categoryGroupId,
      valueInt: -1,
      valueTxt: '',
    })
  );
  if (!answer) {
    // we must revert changes done when we increased or decreased difficulty
    // update workouts category, stats queue saving will be done during this call
    yield call(updateCategoryForWorkout, workoutId, oldCategoryId);
    const title = t('LED.ConfirmChange.RevertConfirm.Title');
    const message = t('LED.ConfirmChange.RevertConfirm.Message');
    yield put(NotificationActions.showSuccessMessage(message, title));
  } else {
    // save stats queue explicitly
    yield put(StatsActions.saveStatsQueue());
    yield call(waitForStatsSaving, 'Difficulty adjustment can not be saved');
    const title = t('LED.ConfirmChange.KeepConfirm.Title');
    const message = t('LED.ConfirmChange.KeepConfirm.Message');
    yield put(NotificationActions.showSuccessMessage(message, title));
  }
  // track user's answer
  yield put(FirebaseActions.ledFollowupFeedbackAction(workoutId, answer, oldCategoryId, currentCategoryId));
  yield put(ExerciseActions.difficultySaved());
}

export function* manualCategoryAdjustment(adjustment: ENeededAdjustment) {
  const workoutId: number = yield select(Selectors.getWorkoutId);
  const categoryGroupId: number = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, workoutId);
  const currentCategoryId: EExerciseCategory = yield select(ConfigSelectors.getWorkoutCategoryId, workoutId);
  // save current category in stats to be able to revert change later
  yield put(
    StatsActions.updateGenericStat(CATEGORY_CHANGE_STAT, {
      statId: categoryGroupId,
      valueInt: currentCategoryId as number,
      valueTxt: '',
    })
  );
  // adjust difficulty, stats queue saving will be done during this call
  yield call(adjustExerciseDifficulty, workoutId, adjustment);
}

export function* increaseDifficultyAnswerSaga(action: PayloadAction<boolean>) {
  const answer = action.payload;
  const t = LanguageService.getTFunction();
  let title = t('LED.IncreaseDifficulty.KeepConfirm.Title');
  let message = t('LED.IncreaseDifficulty.KeepConfirm.Message');
  if (answer) {
    yield call(manualCategoryAdjustment, ENeededAdjustment.Higher);
    title = t('LED.IncreaseDifficulty.ChangeConfirm.Title');
    message = t('LED.IncreaseDifficulty.ChangeConfirm.Message');
  }
  yield put(NotificationActions.showSuccessMessage(message, title));
  // track answer on firebase
  const workoutId: number = yield select(Selectors.getWorkoutId);
  yield put(FirebaseActions.workoutDiffChangeAction(workoutId, answer, ENeededAdjustment.Higher));
  yield put(ExerciseActions.difficultySaved());
}

export function* decreaseDifficultyAnswerSaga(action: PayloadAction<boolean>) {
  const answer = action.payload;
  const t = LanguageService.getTFunction();
  let title = t('LED.DecreaseDifficulty.KeepConfirm.Title');
  let message = t('LED.DecreaseDifficulty.KeepConfirm.Message');
  if (answer) {
    yield call(manualCategoryAdjustment, ENeededAdjustment.Lower);
    title = t('LED.DecreaseDifficulty.ChangeConfirm.Title');
    message = t('LED.DecreaseDifficulty.ChangeConfirm.Message');
  }
  yield put(NotificationActions.showSuccessMessage(message, title));
  // track answer on firebase
  const workoutId: number = yield select(Selectors.getWorkoutId);
  yield put(FirebaseActions.workoutDiffChangeAction(workoutId, answer, ENeededAdjustment.Lower));
  yield put(ExerciseActions.difficultySaved());
}

export function* loadHighscoresSaga() {
  const sessionId = yield select(CyclesSelectors.getCurrentSessionId);
  if (sessionId === ASSESSMENT_SESSION_ID) {
    //return if we are doing assessment
    return;
  }

  // start loading highscore
  const workoutId = yield select(Selectors.getWorkoutId);
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId);
  const isChallenge: boolean = yield select(ConfigSelectors.isChallengeExerciseId, exerciseId);
  yield put(HighscoresActions.loadHighscores(exerciseId, isChallenge));
}

export function* skipExerciseSaga(action: PayloadAction<number>) {
  const workoutId = action.payload;
  const workoutIndex = yield select(SessionSelectors.getActiveWorkoutRelativeIndex);
  yield put(CyclesActions.skipWorkout(workoutId, workoutIndex));
  yield take(CyclesActions.updateWorkoutStatusDone);
  const nextWorkoutIndex = yield select(SessionSelectors.getNextWorkoutIndex);
  if (nextWorkoutIndex !== -1) {
    const nextWorkoutId = yield select(SessionSelectors.getNextWorkoutId);
    const exerciseLink = yield select(SessionSelectors.getWorkoutExerciseIdForLink, nextWorkoutId);
    yield call(redirectInternal, exerciseLink, { index: nextWorkoutIndex });
    return;
  }
  yield put(ExerciseActions.completeSession());
}

// this saga handles repeat exercise - when user chooses to repeat an exercise after it was finished
// should reset state as if exercise were not started and start exercise right away
export function* repeatExerciseSaga() {
  // update progress bar to roll back completed state
  let currentIndex: number = yield select(SessionSelectors.getActiveWorkoutRelativeIndex);
  const workoutsStatus: boolean[] = yield select(SessionSelectors.getCompletedState);
  if (currentIndex < 0) {
    // set current index to last + 1 to make it possible to repeat the last workout
    currentIndex = workoutsStatus.length;
  }
  if (currentIndex > 0) {
    // index = 0 means that we do not need to rollback completed status
    const newStatus = [...workoutsStatus];
    newStatus[currentIndex - 1] = false;
    yield put(SessionActions.updateCompleted(newStatus));
  }
  yield call(resetExerciseState);

  // start exercise immediately with start difficulty received from the last progress entry (just finished exercise)
  const workoutId: number = yield select(Selectors.getWorkoutId);

  // update exerciseRun value if needed
  if (WORKOUTS_SUPPORTING_EXERCISE_RUN.includes(workoutId)) {
    const exerciseRun: number = yield select(Selectors.getExerciseRun);
    yield put(ExerciseActions.setExerciseRun(exerciseRun + 1));
  }

  const lastDifficulty = yield select(ProgressSelectors.getLastWorkoutDifficulty, workoutId);
  yield put(ExerciseActions.startExercise(lastDifficulty));
}

// this saga handles restart exercise (when moderator quit exercise via menu)
// and also used by repeat exercise - i.e. it will run in both cases
// should reset state as if exercise were not started
export function* resetExerciseState() {
  // reinitialize difficulty values
  //const activeDifficulty: number = yield select(Selectors.getDifficulty);
  const startingDifficulty: number = yield select(Selectors.getWorkoutStartDifficulty);
  const reachedDifficulty: number = yield select(Selectors.getWorkoutReachedDifficulty);
  //yield put(Actions.initDifficultyAction(activeDifficulty ?? startingDifficulty, reachedDifficulty));
  // See https://neuronation.atlassian.net/browse/BW-6765 - last decision to keep starting difficulty instead of user selected to be consistent with App
  yield put(ExerciseActions.initDifficulty(startingDifficulty, reachedDifficulty));

  const workoutId: number = yield select(Selectors.getWorkoutId);
  // calculate goal
  const goal = yield select(SessionSelectors.getCurrentWorkoutGoal, workoutId);
  yield put(ExerciseActions.setGoal(goal));

  // clear achievements
  yield put(AchievementsActions.clearAchievements());
}

export function* redirectToSubscribeSaga() {
  const token = yield select(AuthSelectors.getToken);
  const language = yield select(LanguageSelectors.getLanguage);
  yield call(redirectToSubscribe, language, token);
}

export function* completeFirstSessionSaga() {
  yield put(FirebaseActions.postFirstSessionOpened());
}

export function* completeSessionSaga() {
  const sessionId = yield select(CyclesSelectors.getCurrentSessionId);
  // tell cycle feature to complete session even if there uncompleted workouts
  yield put(CyclesActions.finishCurrentSession());
  yield take(CyclesActions.currentSessionFinished);
  // track session complete in firebase
  yield put(FirebaseActions.completeSessionAction(sessionId));
}

export function* trackStartSessionSaga() {
  // check if session is just started
  const isSessionStarted = yield select(SessionSelectors.isSessionStart);
  if (!isSessionStarted) {
    return;
  }
  // track start session
  const sessionId = yield select(CyclesSelectors.getCurrentSessionId);
  yield put(FirebaseActions.startSessionAction(sessionId));
}

export function* waitForFeedbackSaga() {
  // wait for post feedback action
  // saga will be canceled if new progress will start saving without feedback for previous workout
  const result: PayloadAction<IInternalFeedbackResult> = yield take(ExerciseActions.postWorkoutFeedback);
  const feedback = result.payload;
  const workoutId = yield select(Selectors.getWorkoutId);
  const exerciseId: string = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId);
  const completionCount = yield select(ProgressSelectors.getWorkoutCompletionCount, workoutId);
  const startDifficulty: number = yield select(ProgressSelectors.getLastWorkoutStartDifficulty, workoutId);
  const endDifficulty: number = yield select(ProgressSelectors.getLastWorkoutDifficulty, workoutId);
  const badRuns: number = yield select(ProgressSelectors.getLastWorkoutBadRuns, workoutId);
  const goodRuns: number = yield select(ProgressSelectors.getLastWorkoutGoodRuns, workoutId);
  const fullFeedback: IFullFeedbackResult = {
    ...feedback,
    exerciseId,
    startDifficulty,
    endDifficulty,
    badRuns,
    goodRuns,
    completionCount,
  };
  yield put(FirebaseActions.workoutFeedbackAction(fullFeedback));
}

export function* extraModesHandlingSaga(action: PayloadAction<number>) {
  const difficulty = action.payload;
  // wait for mode selected by user
  const { payload: mode }: PayloadAction<EExtraMode> = yield take(ExerciseActions.selectExtraMode);
  // handle selected mode according to current requirements (see BW-5386)
  switch (mode) {
    case EExtraMode.Learning: {
      yield put(ExerciseActions.startExerciseLearning(difficulty));
      break;
    }
    case EExtraMode.Challenger:
    case EExtraMode.Survival: {
      yield put(ExerciseActions.changeView(EExerciseView.NotImplemented));
      break;
    }
    default: {
      // switch back to intro view
      yield put(ExerciseActions.changeView(EExerciseView.Intro));
      break;
    }
  }
}

export function* restartUnitySaga() {
  const isExercisePage = isOnPage('exercise');
  if (!isExercisePage) {
    return;
  }

  const exerciseMode = yield select(Selectors.getExerciseMode);

  if (!exerciseMode) {
    return;
  }

  const difficulty = yield select(Selectors.getDifficulty);

  switch (exerciseMode) {
    case EExerciseMode.Learning:
      yield put(ExerciseActions.startExerciseLearning(difficulty));
      return;
    case EExerciseMode.Training:
      yield put(ExerciseActions.startExercise(difficulty));
      return;
    default:
      return;
  }
}

export default function* exercisePageWatcher() {
  yield all([
    takeEvery(ExerciseActions.openExercise, openExercisePageSaga),
    takeEvery(ExerciseActions.startExercise, startExerciseSaga),
    takeEvery(ExerciseActions.skipExercise, skipExerciseSaga),
    takeEvery(ExerciseActions.repeatExercise, repeatExerciseSaga),
    takeEvery(ExerciseActions.restartExercise, resetExerciseState),
    takeEvery(UnityActions.exerciseComplete, completeWorkoutSaga),
    takeEvery(UnityActions.unityRestarted, restartUnitySaga),
    // we wait until progress is saved before we load highscores
    takeLatest(ProgressActions.endSaveProgress, loadHighscoresSaga),
    takeLatest(ExerciseActions.unlockPremium, redirectToSubscribeSaga),
    takeEvery(ExerciseActions.completeFirstSession, completeFirstSessionSaga),
    takeEvery(ExerciseActions.completeSession, completeSessionSaga),
    takeEvery(ExerciseActions.startExercise, trackStartSessionSaga),
    takeLatest(ProgressActions.saveProgress, waitForFeedbackSaga),
    takeEvery(ExerciseActions.startExerciseLearning, startLearningModeSaga),
    takeLatest(ExerciseActions.difficultyChangeConfirmationAnswer, saveDifficultyChangeAnswerSaga),
    takeLatest(ExerciseActions.difficultyIncreaseAnswer, increaseDifficultyAnswerSaga),
    takeLatest(ExerciseActions.difficultyDecreaseAnswer, decreaseDifficultyAnswerSaga),
    takeLeading(ExerciseActions.openExtraModes, extraModesHandlingSaga),
  ]);
}
